import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { GOOGLE_DOCS } from '@/lib/docs'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'

/*
 * Settings consolidation, batch 2 (2026-09-15). Both pickers lived on
 * `pages/seo/SearchPerformance`, beside the figures they change. They are opened
 * from Settings › Connections now — the per-site lines on the Search Console
 * and Analytics cards — and Search performance links there.
 */

/**
 * The GA4 picker. Fetched only when opened, because it is a live call to Google.
 *
 * **The tenant never types an id, and that is deliberate.** GA4 exposes two
 * identifiers: `G-XXXXXXXXXX` is the measurement id their tracking snippet
 * carries and the one they will reach for, while the API addresses a property by
 * its numeric id. They are not convertible, so a text box would collect the wrong
 * one from most people and fail with a 404 that explains nothing.
 */
export function Ga4Picker({
  siteId,
  domain,
  current,
  onChosen,
}: {
  siteId: string
  domain: string
  current: string | null
  onChosen: () => void
}): JSX.Element {
  const properties = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/analytics-properties', {
          params: { path: { site_id: siteId } },
          signal,
        }),
      ),
    [siteId],
  )
  const choose = useMutation((propertyId: string) =>
    call(() =>
      api.PUT('/api/v1/seo/sites/{site_id}/analytics-property', {
        params: { path: { site_id: siteId } },
        body: { property_id: propertyId },
      }),
    ),
  )

  if (properties.state.status === 'loading') return <Loading label="Asking Google" />
  if (properties.state.status === 'error') {
    return <ErrorState failure={properties.state.failure} onRetry={properties.refetch} />
  }

  const listed = properties.state.data.properties
  if (listed.length === 0) {
    return (
      <Empty
        title="This Google account owns no Analytics properties"
        body={`Sign in with the account that has Analytics for ${domain}, or ask whoever manages it to add you as a Viewer in Google Analytics under Admin → Property access management.`}
        action={
          <>
            <Link to="/settings/connections">Connect a different account</Link>
            {' · '}
            <a
              className="ar-link"
              href={GOOGLE_DOCS.analytics}
              target="_blank"
              rel="noreferrer noopener"
            >
              How to get access ↗
            </a>
          </>
        }
      />
    )
  }

  return (
    <div className="ar-panel ar-stack ar-stack--tight">
      <InlineError failure={choose.state.failure} />
      <strong>Which Analytics property is this site?</strong>
      <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
        A GA4 property is named by whoever set it up, so it may not mention {domain} at all. The
        account it belongs to is shown underneath to tell two similar names apart.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {listed.map((property) => (
          <li key={property.property_id} className="app-conn">
            {/* `strong` then `span`, which is the shape `.app-conn__body` lays
                out as two lines. Two `span`s were inline, and the name and the
                account ran together — "augrankaugrank · 549152979" — under a
                sentence promising the account "underneath". */}
            <span className="app-conn__body">
              <strong>
                {property.display_name === '' ? property.property_id : property.display_name}
              </strong>
              <span>
                {property.account_display_name} ·{' '}
                <span className="ar-mono">{property.property_id}</span>
                {property.reportable ? null : ' · this property type reports no data'}
              </span>
            </span>
            <Button
              size="sm"
              tone={property.property_id === current ? 'ghost' : 'primary'}
              disabled={!property.reportable || property.property_id === current}
              busy={choose.pending}
              busyLabel="Saving"
              onClick={() => {
                void (async () => {
                  const result = await choose.run(property.property_id)
                  if (result.ok) onChosen()
                })()
              }}
            >
              {property.property_id === current ? 'Current' : 'Use this'}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}
